import { Box, LinearProgress, Stack, Step, StepButton, Stepper, Typography, useMediaQuery } from '@mui/material';

interface NavStatusBarStep {
  status: string;
  title: string;
  disabled?: boolean;
}

interface NavStatusBarProps {
  steps: NavStatusBarStep[];
  currentStep: number;
  onStepClick: (step: number) => void;
  disabled?: boolean;
  /** Read-only progress presentation; click and disabled behavior applies only to the stepper. */
  condensed?: boolean;
}

export default function NavStatusBar({
  steps,
  currentStep,
  onStepClick,
  disabled = false,
  condensed = false,
}: NavStatusBarProps) {
  const isCompact = useMediaQuery('(max-width:860px)') || steps.length > 10;
  const activeStep = Math.max(0, Math.min(currentStep, steps.length - 1));
  const activeLabel = steps[activeStep]?.title ?? '';

  if (condensed) {
    return (
      <Box sx={{ width: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {activeLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={((activeStep + 1) / Math.max(steps.length, 1)) * 100}
          aria-label={`Training progress: step ${activeStep + 1} of ${steps.length}`}
          sx={{
            height: 8,
            borderRadius: 999,
            backgroundColor: 'rgba(137, 0, 225, 0.12)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              backgroundColor: '#8900e1',
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {isCompact && (
        <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold' }}>
          {activeLabel}
        </Typography>
      )}
      <Box>
        <Stepper alternativeLabel nonLinear activeStep={activeStep}>
          {steps.map((step, index) => (
            <Step
              key={step.status}
              completed={index < activeStep}
              sx={{
                '@media (max-width:430px)': {
                  px: 0,
                },
              }}
            >
              <StepButton
                onClick={() => !disabled && !step.disabled && onStepClick(index)}
                disabled={disabled || step.disabled}
                sx={{
                  color: '#8900e1',
                  '&:hover': { color: '#7600c4' },
                  '& .MuiStepLabel-label': {
                    display: isCompact ? 'none' : 'block',
                  },
                  '& .MuiStepIcon-root.Mui-completed': {
                    color: '#702b9d',
                  },
                  '& .MuiStepIcon-root.Mui-active': {
                    color: '#8900e1',
                  },
                }}
              >
                {!isCompact && step.title}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Box>
  );
}
